import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { copyText } from "@/lib/clipboard";
import {
  Plus, Briefcase, CaretDown, CaretUp, Copy, Trash, TrashSimple, LockKey,
} from "@phosphor-icons/react";
import { SmartDateField, formatDateDisplay } from "@/components/ResourceSection";

/**
 * WorkExperienceSection — vault-native UX for the Work section.
 *
 * Mirrors EducationSection: always inline-editable cards, debounced auto-save,
 * explicit text-labelled buttons (no pencil), collapsible cards. The big
 * difference is the data model — Work has no sub-table; instead it has free-
 * form responsibilities and a comma-separated skills list.
 */

const KIND_OPTIONS = [
  { v: "job",            label: "Job" },
  { v: "apprenticeship", label: "Apprenticeship" },
  { v: "internship",     label: "Internship" },
  { v: "freelance",      label: "Freelance" },
  { v: "volunteer",      label: "Volunteer" },
];

function useDebounced(fn, delay = 700) {
  const ref = useRef(fn);
  const timer = useRef(null);
  useEffect(() => { ref.current = fn; }, [fn]);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  const debounced = (...args) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => ref.current(...args), delay);
  };
  debounced.cancel = () => { if (timer.current) { clearTimeout(timer.current); timer.current = null; } };
  return debounced;
}

function WorkCard({ entry, onChange, onDelete, onCollapseToggle, collapsed }) {
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  const persist = useDebounced(async (next) => {
    setSaving(true);
    try {
      await api.put(`/work/${next.id}`, next);
      setDirty(false);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2500);
    } catch {
      toast.error("Couldn't save changes");
    } finally { setSaving(false); }
  });

  const update = (patch) => {
    const next = { ...entry, ...patch };
    onChange(next);
    setDirty(true);
    persist(next);
  };

  const lockIn = async () => {
    persist.cancel?.();   // flush any pending debounced save — avoid double-PUT
    setSaving(true);
    try {
      await api.put(`/work/${entry.id}`, entry);
      setDirty(false);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2500);
      toast.success("Locked into the vault");
      onCollapseToggle?.(true);
    } catch {
      toast.error("Couldn't lock it in — try again");
    } finally { setSaving(false); }
  };

  const headline = entry.role || "Untitled role";
  const summary = [
    entry.employer,
    entry.location,
    `${formatDateDisplay(entry.start_date)} — ${entry.current ? "present" : formatDateDisplay(entry.end_date)}`.trim() !== "—"
      ? `${formatDateDisplay(entry.start_date)} — ${entry.current ? "present" : formatDateDisplay(entry.end_date)}`
      : null,
    entry.kind && entry.kind !== "job" ? entry.kind : null,
  ].filter(Boolean).join(" · ");

  const copyAll = () => {
    const head =
      `${entry.role || ""} · ${entry.employer || ""}` +
      (entry.location ? `\n${entry.location}` : "") +
      `\n${formatDateDisplay(entry.start_date)} — ${entry.current ? "present" : formatDateDisplay(entry.end_date)}`;
    const body = entry.responsibilities ? `\n\nResponsibilities:\n${entry.responsibilities}` : "";
    const skills = entry.skills ? `\n\nSkills: ${entry.skills}` : "";
    copyText(head + body + skills, "Entry copied");
  };

  return (
    <div className="card-hard rounded-md bg-white overflow-hidden" data-testid={`work-card-${entry.id}`}>
      <div className="flex items-center justify-between gap-3 p-3 border-b-[1.5px] border-[var(--brand-ink)]/10 bg-[var(--brand-bg)]">
        <button onClick={onCollapseToggle}
          className="flex items-center gap-2 flex-1 min-w-0 text-left hover:opacity-70 transition"
          data-testid={`work-collapse-${entry.id}`}>
          {collapsed ? <CaretDown size={16} weight="bold"/> : <CaretUp size={16} weight="bold"/>}
          <div className="min-w-0">
            <p className="font-medium text-sm truncate">{headline}</p>
            <p className="text-xs font-mono-d text-[var(--brand-muted)] truncate">{summary || "no details yet"}</p>
          </div>
        </button>
        <div className="flex items-center gap-2 shrink-0">
          {saving && <span className="font-mono-d text-[10px] text-[var(--brand-muted)]">saving…</span>}
          {!saving && dirty && <span className="font-mono-d text-[10px] text-[var(--brand-muted)]">unsaved</span>}
          {!saving && !dirty && justSaved && <span className="font-mono-d text-[10px] text-[var(--bolt-sage)]">saved</span>}
          <button onClick={copyAll} data-testid={`work-copy-${entry.id}`}
            className="btn-hard bg-white rounded px-2 py-1 text-xs flex items-center gap-1">
            <Copy size={11}/> copy
          </button>
          <button onClick={onDelete} data-testid={`work-delete-${entry.id}`}
            className="btn-hard bg-white rounded px-2 py-1 text-xs flex items-center gap-1 hover:bg-[var(--pastel-peach)]">
            <Trash size={11}/> delete
          </button>
        </div>
      </div>

      {!collapsed && (
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="block">
              <span className="label-tiny block mb-1">Role / Job title</span>
              <input data-testid={`work-role-${entry.id}`}
                value={entry.role || ""}
                onChange={(e) => update({ role: e.target.value })}
                placeholder="e.g. Senior Marketing Manager"
                className="w-full card-hard rounded-md px-3 py-2 bg-white text-sm"/>
            </label>
            <label className="block">
              <span className="label-tiny block mb-1">Employer</span>
              <input data-testid={`work-employer-${entry.id}`}
                value={entry.employer || ""}
                onChange={(e) => update({ employer: e.target.value })}
                placeholder="e.g. Acme Holdings Ltd"
                className="w-full card-hard rounded-md px-3 py-2 bg-white text-sm"/>
            </label>
            <label className="block">
              <span className="label-tiny block mb-1">Location</span>
              <input data-testid={`work-location-${entry.id}`}
                value={entry.location || ""}
                onChange={(e) => update({ location: e.target.value })}
                placeholder="e.g. London, UK (remote)"
                className="w-full card-hard rounded-md px-3 py-2 bg-white text-sm"/>
            </label>
            <label className="block">
              <span className="label-tiny block mb-1">Type</span>
              <select data-testid={`work-kind-${entry.id}`}
                value={entry.kind || "job"}
                onChange={(e) => update({ kind: e.target.value })}
                className="w-full card-hard rounded-md px-3 py-2 bg-white text-sm">
                {KIND_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                {/* Preserve any legacy/capitalised value the dropdown's hard-coded options don't cover
                    so the visible selection reflects what's stored — prevents silent overwrite. */}
                {entry.kind && !KIND_OPTIONS.some((o) => o.v === entry.kind) && (
                  <option value={entry.kind}>{entry.kind}</option>
                )}
              </select>
            </label>
            <div>
              <span className="label-tiny block mb-1">From</span>
              <SmartDateField
                resource="work" fieldKey="start_date"
                value={entry.start_date || ""}
                onChange={(v) => update({ start_date: v })}
              />
            </div>
            <div>
              <span className="label-tiny block mb-1 flex items-center justify-between gap-2">
                <span>To</span>
                <label className="inline-flex items-center gap-1.5 text-[10px] font-normal cursor-pointer">
                  <input type="checkbox"
                    data-testid={`work-current-${entry.id}`}
                    checked={!!entry.current}
                    onChange={(e) => update({ current: e.target.checked, end_date: e.target.checked ? "" : entry.end_date })}
                    className="accent-[var(--brand-primary)]"/>
                  this is my current role
                </label>
              </span>
              {entry.current ? (
                <div className="card-hard rounded-md px-3 py-2 bg-[var(--brand-bg)] text-sm font-mono-d text-[var(--brand-muted)]">
                  present
                </div>
              ) : (
                <SmartDateField
                  resource="work" fieldKey="end_date"
                  value={entry.end_date || ""}
                  onChange={(v) => update({ end_date: v })}
                />
              )}
            </div>
          </div>

          {/* Responsibilities */}
          <label className="block">
            <span className="label-tiny block mb-1">Responsibilities / what you did</span>
            <textarea data-testid={`work-resp-${entry.id}`}
              value={entry.responsibilities || ""}
              onChange={(e) => update({ responsibilities: e.target.value })}
              placeholder="describe what you did, your scope, and any standout impact. plain text — paste straight into application forms."
              className="w-full card-hard rounded-md px-3 py-2 bg-white text-sm min-h-[120px]"/>
          </label>

          {/* Skills */}
          <label className="block">
            <span className="label-tiny block mb-1">Skills used</span>
            <input data-testid={`work-skills-${entry.id}`}
              value={entry.skills || ""}
              onChange={(e) => update({ skills: e.target.value })}
              placeholder="e.g. SQL, stakeholder management, Tableau, B2B sales"
              className="w-full card-hard rounded-md px-3 py-2 bg-white text-sm"/>
            <p className="font-mono-d text-[10px] text-[var(--brand-muted)] mt-1">
              comma-separated · used by the autofill, ATS check, and resume tailor.
            </p>
          </label>

          {/* Lock-it-in CTA */}
          <div className="pt-3 border-t-[1.5px] border-[var(--brand-ink)]/10 flex items-center justify-between gap-3">
            <p className="font-mono-d text-[10px] text-[var(--brand-muted)]">
              auto-saves as you type · click <b>lock it in</b> when you&apos;re done
            </p>
            <button onClick={lockIn} disabled={saving}
              data-testid={`work-lock-${entry.id}`}
              className="btn-hard bg-[var(--brand-ink)] text-white rounded-md px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-60">
              <LockKey size={14} weight="bold"/> {saving ? "locking…" : "lock it in"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function WorkExperienceSection() {
  const [entries, setEntries] = useState([]);
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/work");
      setEntries(data);
      // Land collapsed by default for tidiness — user can expand any card.
      setCollapsed(new Set(data.map((e) => e.id)));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/work", {
        role: "New role",
        employer: "Add employer",
        kind: "job",
      });
      setEntries((cur) => [data, ...cur]);
      // New cards should open expanded so the user can start typing immediately.
      setCollapsed((cur) => {
        const next = new Set(cur);
        next.delete(data.id);
        return next;
      });
      toast.success("New role added — start filling it in");
    } catch {
      toast.error("Couldn't add entry");
    } finally { setCreating(false); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this work entry?")) return;
    try {
      await api.delete(`/work/${id}`);
      setEntries((cur) => cur.filter((e) => e.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Couldn't delete");
    }
  };

  const clearAll = async () => {
    if (entries.length === 0) return;
    if (!window.confirm(`Delete all ${entries.length} work entries? This can't be undone.`)) return;
    try {
      const { data } = await api.delete("/vault/clear/work");
      setEntries([]);
      toast.success(`Wiped ${data.deleted} work entries`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't clear");
    }
  };

  const toggleCollapse = (id, force) => {
    setCollapsed((cur) => {
      const next = new Set(cur);
      if (force === true) next.add(id);
      else if (force === false) next.delete(id);
      else if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const allCollapsed = entries.length > 0 && collapsed.size === entries.length;
  const collapseAll  = () => setCollapsed(new Set(entries.map((e) => e.id)));
  const expandAll    = () => setCollapsed(new Set());

  return (
    <div className="card-hard rounded-md bg-white p-5 relative overflow-hidden" data-testid="work-section">
      <span aria-hidden="true" className="absolute left-0 top-0 bottom-0 w-1.5 bg-[var(--bolt-sage)]"/>
      <div className="flex items-start justify-between gap-3 mb-4 pl-2">
        <div>
          <p className="label-tiny flex items-center gap-1.5 m-0">
            <Briefcase size={12} weight="duotone" className="text-[var(--bolt-sage)]"/> /work
          </p>
          <h2 className="font-display text-2xl tracking-tight mt-1">Work Experience</h2>
          <p className="text-xs text-[var(--brand-muted)] mt-1 max-w-2xl">
            one card per role. fill in what you did, how long for, and the skills you used.
            changes save as you type — click <b>lock it in</b> when each role is fully entered.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="font-mono-d text-xs text-[var(--brand-muted)]">
            {entries.length} {entries.length === 1 ? "role" : "roles"}
          </span>
          {entries.length > 1 && (
            <button onClick={allCollapsed ? expandAll : collapseAll}
              data-testid="work-collapse-all"
              className="btn-hard bg-white rounded-md px-3 py-2 text-xs flex items-center gap-1">
              {allCollapsed ? <CaretDown size={12} weight="bold"/> : <CaretUp size={12} weight="bold"/>}
              {allCollapsed ? "expand all" : "collapse all"}
            </button>
          )}
          {entries.length > 0 && (
            <button onClick={clearAll} data-testid="clear-all-work"
              title={`wipe all ${entries.length} work entries`}
              className="btn-hard bg-[#B91C1C] text-white rounded-md px-2.5 py-2 text-xs flex items-center gap-1.5">
              <TrashSimple size={13} weight="bold"/>
              <span className="hidden sm:inline">delete all</span>
            </button>
          )}
          <button onClick={create} disabled={creating}
            data-testid="add-work"
            className="btn-hard bg-[var(--brand-ink)] text-white rounded-md px-3 py-2 text-sm flex items-center gap-2 disabled:opacity-60">
            <Plus size={14} weight="bold"/> add work
          </button>
        </div>
      </div>

      {loading ? (
        <p className="font-mono-d text-sm text-[var(--brand-muted)]">loading…</p>
      ) : entries.length === 0 ? (
        <div className="card-hard rounded-md p-8 bg-[var(--brand-bg)] text-center" data-testid="work-empty">
          <Briefcase size={32} weight="duotone" className="mx-auto mb-2 text-[var(--brand-muted)]"/>
          <p className="font-display text-lg tracking-tight">no work history yet.</p>
          <p className="text-xs text-[var(--brand-muted)] mt-1">
            click <b>+ add work</b> above. one card per role — job, apprenticeship, internship, freelance, or volunteer.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {entries.map((e) => (
            <WorkCard key={e.id}
              entry={e}
              onChange={(next) => setEntries((cur) => cur.map((x) => x.id === next.id ? next : x))}
              onDelete={() => remove(e.id)}
              onCollapseToggle={(force) => toggleCollapse(e.id, force)}
              collapsed={collapsed.has(e.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
